/**
 * API Configuration
 *
 * Centralized configuration for API services.
 * This configuration is shared across all runtime environments (Browser, SSR, React Native).
 */

import type { BackendService } from '../api/client';

/**
 * Service name type
 * Re-export for convenience
 */
export type ServiceName = BackendService;

/**
 * Staging sub-environments
 * These correspond to different backend environments (dstg, ddev, dpre)
 */
export type StagingEnv = 'dstg' | 'ddev' | 'dpre';

/**
 * Production URLs for each service
 */
const PRODUCTION_URLS: Record<ServiceName, string> = {
  oex: 'https://deriv-web-api.crypto.com',
  bff: 'https://exchange-fe.crypto.com',
  nts: 'https://exchange-nts.crypto.com/fe-ex-api',
  be: 'https://exchange-be.crypto.com',
  worker: 'https://x-web-api-dstg.xweb.3ona.co/',
};

/**
 * Development URLs for each service (uses Vite proxy)
 */
const DEVELOPMENT_URLS: Record<ServiceName, string> = {
  oex: '/deriv',
  bff: '/bff',
  nts: '/fe-ex-api',
  be: '/be',
  worker: '/worker',
};

/**
 * Staging URL patterns for each service
 * Dynamically generates URLs based on staging sub-environment (dstg, ddev, dpre)
 */
const STAGING_URL_PATTERNS: Record<ServiceName, (env: StagingEnv) => string> = {
  oex: (env) => `https://${env}-rest-gateway.x.3ona.co/deriv`,
  // bff: () => 'https://x-web-api-dstg.xweb.3ona.co',
  bff: (env) => `https://${env}-fe.3ona.co`,
  worker: () => 'https://x-web-api-dstg.xweb.3ona.co/',
  nts: (env) => `https://${env}-nts.3ona.co/fe-ex-api`,
  be: (env) => `https://${env}-be.3ona.co`,
};

/**
 * All service names
 * Used for iterating over all services during setup
 */
export const ALL_SERVICES: ServiceName[] = [
  'oex',
  'bff',
  'nts',
  'be',
  'worker',
];

/**
 * Get base URL for a service
 *
 * @param service - Service name
 * @param mode - Runtime environment (development, staging, production)
 * @param stagingEnv - Staging sub-environment, only used when mode is 'staging'
 * @returns Base URL for the service
 *
 * @example
 * // Production
 * getServiceBaseUrl('bff', 'production');
 * // => 'https://exchange-fe.crypto.com'
 *
 * @example
 * // Local development (uses Vite proxy)
 * getServiceBaseUrl('oex', 'development');
 * // => '/deriv'
 *
 * @example
 * // Staging with dstg environment
 * getServiceBaseUrl('oex', 'staging', 'dstg');
 * // => 'https://dstg-rest-gateway.x.3ona.co/deriv'
 */
export function getServiceBaseUrl(
  service: ServiceName,
  mode: string,
  stagingEnv?: StagingEnv,
): string {
  if (mode === 'production') {
    return PRODUCTION_URLS[service];
  }

  if (mode === 'staging') {
    const env = stagingEnv ?? 'dstg';
    return STAGING_URL_PATTERNS[service](env);
  }

  return DEVELOPMENT_URLS[service];
}

/**
 * Check if environment is production
 *
 * @param env - Environment string
 * @returns True if production
 */
export function isProductionEnv(env: string): boolean {
  return env === 'production';
}

/**
 * Check if a string is a valid staging sub-environment
 */
export function isStagingEnv(env: string): env is StagingEnv {
  return env === 'dstg' || env === 'ddev' || env === 'dpre';
}
